import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Lock, LogOut, Search, Download, Trash2, RefreshCw, Instagram, FileText } from 'lucide-react';
import { FaTiktok, FaYoutube, FaWhatsapp } from 'react-icons/fa';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  influsLogin, influsLogout, influsMe,
  listInfluencerLeads, updateInfluencerLead, deleteInfluencerLead,
  exportInfluencerLeadsCsvUrl,
  listMarcasConsultas, updateMarcaConsulta, deleteMarcaConsulta,
  exportMarcasCsvUrl,
} from '../services/influs';
import LoginDiagnostics from '../components/LoginDiagnostics';
import BackToPanelButton, { BackToPanelLink, redirectToHubIfFromHub } from '../components/BackToPanelButton';

const STATUS_OPTIONS = [
  { value: 'pendiente',   label: 'Pendiente',   color: '#9CA3AF' },
  { value: 'interesante', label: 'Interesante', color: '#F4A261' },
  { value: 'contactado',  label: 'Contactado',  color: '#2A9D8F' },
  { value: 'rechazado',   label: 'Rechazado',   color: '#E63946' },
];

const statusMeta = (s) => STATUS_OPTIONS.find((o) => o.value === s) || STATUS_OPTIONS[0];

const formatFecha = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-AR') + ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const whatsappLink = (raw) => {
  if (!raw) return null;
  const digits = String(raw).replace(/[^0-9]/g, '');
  if (digits.length < 8) return null;
  return `https://wa.me/${digits}`;
};

// Build an absolute URL for each social handle so operators can jump straight
// to the influencer's profile from the CRM row.
const socialUrl = (platform, handle) => {
  if (!handle) return null;
  const raw = String(handle).trim();
  if (!raw) return null;
  // Already a full URL? Just return it.
  if (/^https?:\/\//i.test(raw)) return raw;
  const clean = raw.replace(/^@/, '').replace(/^\/+/, '').split(/[\s?]/)[0];
  if (!clean) return null;
  if (platform === 'instagram') return `https://instagram.com/${clean}`;
  if (platform === 'tiktok')    return `https://www.tiktok.com/@${clean}`;
  if (platform === 'youtube')   return `https://youtube.com/@${clean}`;
  return null;
};

const webUrl = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith('@')) return `https://instagram.com/${s.slice(1)}`;
  // Bare domain like "brand.com" → prepend https://
  if (/^[a-z0-9-]+\.[a-z]{2,}/i.test(s)) return `https://${s}`;
  return null;
};

// -------------------- LOGIN SCREEN --------------------
const LoginScreen = ({ onAuthed }) => {
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      await influsLogin(password, remember);
      onAuthed();
    } catch (e2) {
      // Distinguish real 401 (wrong password) from a network-level failure
      // (browser blocked the request, CORS, offline, etc.). The two used to
      // share the same fallback message which was misleading.
      const status = e2?.response?.status;
      if (status === 401) setErr(e2?.response?.data?.detail || 'Contraseña incorrecta');
      else if (status === 429) setErr('Demasiados intentos. Esperá 15 minutos.');
      else if (e2?.response) setErr(e2.response.data?.detail || 'Error al ingresar');
      else setErr('Error de red — no se pudo contactar al servidor');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
      <form onSubmit={submit} className="w-full max-w-sm p-8" data-testid="influs-login-form">
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1B6B3A' }}>
            <Lock size={28} className="text-white" />
          </div>
        </div>
        <h2 className="text-3xl font-bold text-white text-center mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
          Oportunidades
        </h2>
        <p className="text-xs text-gray-500 text-center mb-6">Acceso restringido · Talengers</p>
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Contraseña"
          autoComplete="current-password"
          className="bg-gray-900 border-gray-700 text-white mb-3"
          data-testid="influs-login-password"
        />
        <label className="flex items-center gap-2 mb-3 text-xs text-gray-300 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="accent-green-600 h-4 w-4"
            data-testid="influs-login-remember-checkbox"
          />
          Recordarme durante 7 días en este dispositivo
        </label>
        {err && <p className="text-red-400 text-sm mb-3 text-center" data-testid="influs-login-error">{err}</p>}
        <Button
          type="submit"
          disabled={busy || !password}
          className="w-full text-white font-semibold"
          style={{ backgroundColor: '#1B6B3A' }}
          data-testid="influs-login-submit"
        >
          {busy ? 'Verificando...' : 'Ingresar'}
        </Button>
        <p className="text-[10px] text-gray-600 text-center mt-4" data-testid="build-id">
          build · {(typeof window !== 'undefined' && window.__APP_BUILD_ID) || 'dev'}
        </p>
        <LoginDiagnostics />
        <div className="text-center mt-4">
          <BackToPanelLink />
        </div>
      </form>
    </div>
  );
};

// -------------------- LEAD DETAIL MODAL --------------------
const LeadModal = ({ lead, onClose, onSaved }) => {
  const [status, setStatus] = useState(lead?.status || 'pendiente');
  const [notas, setNotas] = useState(lead?.notas || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStatus(lead?.status || 'pendiente');
    setNotas(lead?.notas || '');
  }, [lead]);

  if (!lead) return null;

  const save = async () => {
    setSaving(true);
    try {
      await updateInfluencerLead(lead.id, { status, notas });
      onSaved({ ...lead, status, notas });
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const wa = whatsappLink(lead.celular);

  return (
    <Dialog open={!!lead} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl bg-white" data-testid="lead-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            {lead.nombre}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div><span className="text-gray-500">Email:</span> <a href={`mailto:${lead.email}`} className="text-blue-600 hover:underline">{lead.email}</a></div>
          <div>
            <span className="text-gray-500">Celular:</span>{' '}
            {wa ? <a href={wa} target="_blank" rel="noreferrer" className="text-green-700 hover:underline inline-flex items-center gap-1"><FaWhatsapp />{lead.celular}</a> : lead.celular}
          </div>
          <div><span className="text-gray-500">País / Ciudad:</span> {lead.pais}{lead.ciudad ? ` · ${lead.ciudad}` : ''}</div>
          <div><span className="text-gray-500">Edad:</span> {lead.edad || '—'}</div>
          <div><span className="text-gray-500">Fecha:</span> {formatFecha(lead.fecha)}</div>
          <div><span className="text-gray-500">Verticales:</span> {(lead.verticales || []).join(', ') || '—'}</div>
        </div>

        <div className="border-t pt-3 mt-2">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Redes sociales</h4>
          <div className="space-y-1.5 text-sm">
            {lead.instagram_handle ? (
              <div className="flex items-center gap-2">
                <Instagram size={14} style={{ color: '#E4405F' }} />
                <a href={socialUrl('instagram', lead.instagram_handle)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline" data-testid="modal-link-instagram">
                  {lead.instagram_handle}
                </a>
                <span className="text-gray-500">· {lead.instagram_followers || '—'}</span>
              </div>
            ) : null}
            {lead.tiktok_handle ? (
              <div className="flex items-center gap-2">
                <FaTiktok size={14} />
                <a href={socialUrl('tiktok', lead.tiktok_handle)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline" data-testid="modal-link-tiktok">
                  {lead.tiktok_handle}
                </a>
                <span className="text-gray-500">· {lead.tiktok_followers || '—'}</span>
              </div>
            ) : null}
            {lead.youtube_handle ? (
              <div className="flex items-center gap-2">
                <FaYoutube size={14} style={{ color: '#FF0000' }} />
                <a href={socialUrl('youtube', lead.youtube_handle)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline" data-testid="modal-link-youtube">
                  {lead.youtube_handle}
                </a>
                <span className="text-gray-500">· {lead.youtube_followers || '—'}</span>
              </div>
            ) : null}
            {lead.source === 'legacy_contacto' && lead.redes_legacy ? (
              <div className="text-gray-700 italic">Redes (texto libre del form viejo): {lead.redes_legacy}</div>
            ) : null}
            {lead.source === 'legacy_contacto' && !lead.redes_legacy && !lead.instagram_handle && !lead.tiktok_handle && !lead.youtube_handle ? (
              <div className="text-gray-500 italic text-xs">Solicitud antigua sin redes (form viejo)</div>
            ) : null}
          </div>
        </div>

        {lead.mensaje ? (
          <div className="border-t pt-3">
            <h4 className="text-sm font-semibold text-gray-700 mb-1">Mensaje</h4>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{lead.mensaje}</p>
          </div>
        ) : null}

        <div className="border-t pt-3 grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
          <div>
            <label className="text-xs font-semibold uppercase text-gray-500">Estado</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="lead-modal-status-trigger" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-gray-500">Notas internas</label>
            <Textarea value={notas} onChange={(e) => setNotas(e.target.value)} rows={3} className="mt-1" data-testid="lead-modal-notas" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="lead-modal-cancel">Cancelar</Button>
          <Button onClick={save} disabled={saving} className="text-white" style={{ backgroundColor: '#1B6B3A' }} data-testid="lead-modal-save">
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// -------------------- CRM TABLE --------------------
const CRMTable = ({ onLogout }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('__ALL__');
  const [verticalFilter, setVerticalFilter] = useState('__ALL__');
  const [paisFilter, setPaisFilter] = useState('__ALL__');
  const [selected, setSelected] = useState(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const data = await listInfluencerLeads();
      setLeads(Array.isArray(data?.leads) ? data.leads : []);
    } catch (e) {
      if (e?.response?.status === 401) {
        onLogout();
        return;
      }
      // Surface the error to the user instead of silently showing "0 solicitudes".
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || 'Error desconocido';
      setFetchError(status ? `HTTP ${status} — ${detail}` : `Fallo de red — ${detail}`);
      setLeads([]);
    } finally { setLoading(false); }
  }, [onLogout]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const verticales = useMemo(() => {
    const s = new Set();
    leads.forEach((l) => (l.verticales || []).forEach((v) => s.add(v)));
    return Array.from(s).sort();
  }, [leads]);

  const paises = useMemo(() => {
    const s = new Set();
    leads.forEach((l) => { if (l.pais) s.add(l.pais); });
    return Array.from(s).sort();
  }, [leads]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (statusFilter !== '__ALL__' && (l.status || 'pendiente') !== statusFilter) return false;
      if (verticalFilter !== '__ALL__' && !(l.verticales || []).includes(verticalFilter)) return false;
      if (paisFilter !== '__ALL__' && l.pais !== paisFilter) return false;
      if (!q) return true;
      const blob = [
        l.nombre, l.email, l.celular, l.ciudad, l.pais,
        l.instagram_handle, l.tiktok_handle, l.youtube_handle,
        (l.verticales || []).join(' '),
      ].filter(Boolean).join(' ').toLowerCase();
      return blob.includes(q);
    });
  }, [leads, search, statusFilter, verticalFilter, paisFilter]);

  const counts = useMemo(() => {
    const c = { total: leads.length, pendiente: 0, interesante: 0, contactado: 0, rechazado: 0 };
    leads.forEach((l) => { c[l.status || 'pendiente'] = (c[l.status || 'pendiente'] || 0) + 1; });
    return c;
  }, [leads]);

  const changeStatus = async (lead, newStatus) => {
    setLeads((arr) => arr.map((l) => l.id === lead.id ? { ...l, status: newStatus } : l));
    try { await updateInfluencerLead(lead.id, { status: newStatus }); }
    catch { fetchLeads(); }
  };

  const removeLead = async (lead) => {
    if (!window.confirm(`¿Eliminar el lead de ${lead.nombre}?`)) return;
    try {
      await deleteInfluencerLead(lead.id);
      setLeads((arr) => arr.filter((l) => l.id !== lead.id));
    } catch { /* ignore */ }
  };

  const exportCsv = () => {
    // Open in a new tab so the browser handles the file download with auth cookie
    window.open(exportInfluencerLeadsCsvUrl, '_blank');
  };

  return (
    <div data-testid="talentos-view">
      {/* Section sub-header */}
      <div className="px-6 py-4 flex flex-wrap items-center justify-between gap-3 border-b border-gray-800">
        <div>
          <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            Solicitudes de Talentos
          </h2>
          <p className="text-xs text-gray-500" data-testid="leads-counter">
            {counts.total} solicitudes · {counts.pendiente} pendientes · {counts.interesante} interesantes · {counts.contactado} contactados · {counts.rechazado} rechazados
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Button onClick={fetchLeads} variant="outline" size="sm" className="bg-gray-900 border-gray-700 text-white hover:bg-gray-800" data-testid="refresh-btn">
            <RefreshCw size={14} className="mr-1" />Actualizar
          </Button>
          <Button onClick={exportCsv} size="sm" className="text-white" style={{ backgroundColor: '#2A9D8F' }} data-testid="export-csv-btn">
            <Download size={14} className="mr-1" />Exportar CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="relative md:col-span-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, email, @user..."
            className="pl-9 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
            data-testid="leads-search"
          />
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="bg-gray-900 border-gray-700 text-white" data-testid="status-filter">
            <SelectValue placeholder="Todos los estados" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__ALL__">Todos los estados</SelectItem>
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={verticalFilter} onValueChange={setVerticalFilter}>
          <SelectTrigger className="bg-gray-900 border-gray-700 text-white" data-testid="vertical-filter">
            <SelectValue placeholder="Todas las verticales" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__ALL__">Todas las verticales</SelectItem>
            {verticales.map((v) => (<SelectItem key={v} value={v}>{v}</SelectItem>))}
          </SelectContent>
        </Select>

        <Select value={paisFilter} onValueChange={setPaisFilter}>
          <SelectTrigger className="bg-gray-900 border-gray-700 text-white" data-testid="pais-filter">
            <SelectValue placeholder="Todos los países" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__ALL__">Todos los países</SelectItem>
            {paises.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {/* Error banner — visible when fetchLeads failed. Prevents users from
          confusing a network failure with an actually empty inbox. */}
      {fetchError && (
        <div className="mx-6 mb-4 rounded-lg border border-red-700/60 bg-red-900/30 p-4 flex items-start gap-3" data-testid="leads-fetch-error">
          <div className="mt-0.5">⚠️</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-red-200">No se pudieron cargar las solicitudes</div>
            <div className="text-xs text-red-300/80 mt-1 break-words">{fetchError}</div>
            <div className="text-[11px] text-gray-400 mt-2">
              Puede ser un problema temporal de red o que tu antivirus esté bloqueando la petición.
              Verificá tu conexión y volvé a intentar. Si el problema persiste, mandanos captura de este mensaje.
            </div>
          </div>
          <Button onClick={fetchLeads} size="sm" variant="outline" className="border-red-700 text-red-100 hover:bg-red-800/40 shrink-0" data-testid="leads-fetch-retry">
            <RefreshCw size={14} className="mr-1" />Reintentar
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="px-6 pb-12">
        <div className="overflow-x-auto border border-gray-800 rounded-lg">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="bg-gray-900">
              <tr className="text-left text-gray-400 uppercase text-xs">
                <th className="px-3 py-3">Fecha</th>
                <th className="px-3 py-3">Nombre</th>
                <th className="px-3 py-3">Contacto</th>
                <th className="px-3 py-3">Ubicación</th>
                <th className="px-3 py-3 w-52">Redes (seguidores)</th>
                <th className="px-3 py-3">Verticales</th>
                <th className="px-3 py-3">Estado</th>
                <th className="px-3 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="text-gray-200">
              {loading && (
                <tr><td colSpan={8} className="text-center text-gray-500 py-10">Cargando...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center text-gray-500 py-10" data-testid="leads-empty">
                  No hay solicitudes con esos filtros
                </td></tr>
              )}
              {!loading && filtered.map((l) => {
                const meta = statusMeta(l.status);
                const wa = whatsappLink(l.celular);
                return (
                  <tr
                    key={l.id}
                    className="border-t border-gray-800 hover:bg-gray-900/50 cursor-pointer"
                    onClick={() => setSelected(l)}
                    data-testid={`lead-row-${l.id}`}
                  >
                    <td className="px-3 py-3 text-xs text-gray-400 whitespace-nowrap">{formatFecha(l.fecha)}</td>
                    <td className="px-3 py-3 font-medium">
                      {l.nombre}
                      <div className="text-xs text-gray-500 flex items-center gap-2">
                        <span>Edad: {l.edad || '—'}</span>
                        {l.source === 'legacy_contacto' && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold" style={{ backgroundColor: '#F4A26122', color: '#F4A261' }} data-testid={`legacy-badge-${l.id}`}>
                            Form viejo
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs">
                      <div>{l.email}</div>
                      <div className="text-gray-400">
                        {wa ? (
                          <a href={wa} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-green-400 hover:underline inline-flex items-center gap-1">
                            <FaWhatsapp />{l.celular}
                          </a>
                        ) : l.celular}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs">{l.pais}{l.ciudad ? <div className="text-gray-500">{l.ciudad}</div> : null}</td>
                    <td className="px-3 py-3 text-xs w-52 max-w-[13rem]" onClick={(e) => { /* icons/links inside stop propagation */ }}>
                      {l.instagram_handle && (
                        <div className="flex items-center gap-1 min-w-0"><Instagram size={12} style={{ color: '#E4405F', flexShrink: 0 }} />
                          <a href={socialUrl('instagram', l.instagram_handle)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-sky-400 hover:underline truncate" data-testid={`row-link-ig-${l.id}`} title={l.instagram_handle}>
                            {l.instagram_handle}
                          </a>
                          <span className="text-gray-500 shrink-0">· {l.instagram_followers || '—'}</span>
                        </div>
                      )}
                      {l.tiktok_handle && (
                        <div className="flex items-center gap-1 min-w-0"><FaTiktok size={11} style={{ flexShrink: 0 }} />
                          <a href={socialUrl('tiktok', l.tiktok_handle)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-sky-400 hover:underline truncate" data-testid={`row-link-tt-${l.id}`} title={l.tiktok_handle}>
                            {l.tiktok_handle}
                          </a>
                          <span className="text-gray-500 shrink-0">· {l.tiktok_followers || '—'}</span>
                        </div>
                      )}
                      {l.youtube_handle && (
                        <div className="flex items-center gap-1 min-w-0"><FaYoutube size={12} style={{ color: '#FF0000', flexShrink: 0 }} />
                          <a href={socialUrl('youtube', l.youtube_handle)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-sky-400 hover:underline truncate" data-testid={`row-link-yt-${l.id}`} title={l.youtube_handle}>
                            {l.youtube_handle}
                          </a>
                          <span className="text-gray-500 shrink-0">· {l.youtube_followers || '—'}</span>
                        </div>
                      )}
                      {l.source === 'legacy_contacto' && l.redes_legacy && (
                        <div className="text-gray-400 italic truncate" title={l.redes_legacy}>{l.redes_legacy}</div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-xs">
                      <div className="flex flex-wrap gap-1">
                        {(l.verticales || []).slice(0, 4).map((v) => (
                          <span key={v} className="px-1.5 py-0.5 rounded bg-gray-800 text-gray-300">{v}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <Select value={l.status || 'pendiente'} onValueChange={(v) => changeStatus(l, v)}>
                        <SelectTrigger
                          className="h-8 text-xs border-0 font-semibold"
                          style={{ backgroundColor: meta.color + '22', color: meta.color }}
                          data-testid={`lead-status-${l.id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" onClick={() => removeLead(l)} data-testid={`lead-delete-${l.id}`} className="text-red-400 hover:text-red-300 hover:bg-red-950/30">
                        <Trash2 size={14} />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <LeadModal
        lead={selected}
        onClose={() => setSelected(null)}
        onSaved={(updated) => {
          setLeads((arr) => arr.map((l) => l.id === updated.id ? updated : l));
          setSelected(null);
        }}
      />
    </div>
  );
};

// -------------------- MARCA DETAIL MODAL --------------------
const MarcaModal = ({ marca, onClose, onSaved }) => {
  const [status, setStatus] = useState(marca?.status || 'pendiente');
  const [notas, setNotas] = useState(marca?.notas || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStatus(marca?.status || 'pendiente');
    setNotas(marca?.notas || '');
  }, [marca]);

  if (!marca) return null;

  const save = async () => {
    setSaving(true);
    try {
      await updateMarcaConsulta(marca.id, { status, notas });
      onSaved({ ...marca, status, notas });
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!marca} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl bg-white" data-testid="marca-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            {marca.nombre}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div><span className="text-gray-500">Empresa / Agencia:</span>{' '}
            <span className="font-medium" data-testid="marca-modal-empresa">{marca.empresa || '—'}</span>
          </div>
          <div><span className="text-gray-500">Email:</span> <a href={`mailto:${marca.email}`} className="text-blue-600 hover:underline">{marca.email}</a></div>
          <div><span className="text-gray-500">Teléfono:</span>{' '}
            {marca.telefono ? (
              whatsappLink(marca.telefono)
                ? <a href={whatsappLink(marca.telefono)} target="_blank" rel="noreferrer" className="text-green-700 hover:underline inline-flex items-center gap-1"><FaWhatsapp />{marca.telefono}</a>
                : marca.telefono
            ) : '—'}
          </div>
          <div><span className="text-gray-500">País:</span> {marca.pais || '—'}</div>
          <div>
            <span className="text-gray-500">Web / RRSS:</span>{' '}
            {marca.web ? (
              webUrl(marca.web)
                ? <a href={webUrl(marca.web)} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline" data-testid="marca-modal-link-web">{marca.web}</a>
                : marca.web
            ) : '—'}
          </div>
          <div><span className="text-gray-500">Fecha:</span> {formatFecha(marca.fecha)}</div>
          <div className="md:col-span-2"><span className="text-gray-500">Asunto:</span> <span className="font-medium">{marca.asunto || '—'}</span></div>
        </div>
        {marca.mensaje ? (
          <div className="border-t pt-3">
            <h4 className="text-sm font-semibold text-gray-700 mb-1">Mensaje</h4>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{marca.mensaje}</p>
          </div>
        ) : null}
        <div className="border-t pt-3 grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
          <div>
            <label className="text-xs font-semibold uppercase text-gray-500">Estado</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="marca-modal-status-trigger" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-gray-500">Notas internas</label>
            <Textarea value={notas} onChange={(e) => setNotas(e.target.value)} rows={3} className="mt-1" data-testid="marca-modal-notas" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="marca-modal-cancel">Cancelar</Button>
          <Button onClick={save} disabled={saving} className="text-white" style={{ backgroundColor: '#457B9D' }} data-testid="marca-modal-save">
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// -------------------- MARCAS TABLE --------------------
const MarcasTable = ({ onLogout }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('__ALL__');
  const [selected, setSelected] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const data = await listMarcasConsultas();
      setItems(Array.isArray(data?.marcas) ? data.marcas : []);
    } catch (e) {
      if (e?.response?.status === 401) {
        onLogout();
        return;
      }
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || 'Error desconocido';
      setFetchError(status ? `HTTP ${status} — ${detail}` : `Fallo de red — ${detail}`);
      setItems([]);
    } finally { setLoading(false); }
  }, [onLogout]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (statusFilter !== '__ALL__' && (it.status || 'pendiente') !== statusFilter) return false;
      if (!q) return true;
      const blob = [it.nombre, it.empresa, it.email, it.asunto, it.mensaje].filter(Boolean).join(' ').toLowerCase();
      return blob.includes(q);
    });
  }, [items, search, statusFilter]);

  const counts = useMemo(() => {
    const c = { total: items.length, pendiente: 0, interesante: 0, contactado: 0, rechazado: 0 };
    items.forEach((it) => { const s = it.status || 'pendiente'; c[s] = (c[s] || 0) + 1; });
    return c;
  }, [items]);

  const changeStatus = async (item, newStatus) => {
    setItems((arr) => arr.map((x) => x.id === item.id ? { ...x, status: newStatus } : x));
    try { await updateMarcaConsulta(item.id, { status: newStatus }); }
    catch { fetchItems(); }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`¿Eliminar la consulta de ${item.nombre}?`)) return;
    try {
      await deleteMarcaConsulta(item.id);
      setItems((arr) => arr.filter((x) => x.id !== item.id));
    } catch { /* ignore */ }
  };

  const exportCsv = () => window.open(exportMarcasCsvUrl, '_blank');

  return (
    <div data-testid="marcas-view">
      {/* Section sub-header */}
      <div className="px-6 py-4 flex flex-wrap items-center justify-between gap-3 border-b border-gray-800">
        <div>
          <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            Consultas de Marcas / Agencias
          </h2>
          <p className="text-xs text-gray-500" data-testid="marcas-counter">
            {counts.total} consultas · {counts.pendiente} pendientes · {counts.interesante} interesantes · {counts.contactado} contactados · {counts.rechazado} rechazados
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Button onClick={fetchItems} variant="outline" size="sm" className="bg-gray-900 border-gray-700 text-white hover:bg-gray-800" data-testid="marcas-refresh-btn">
            <RefreshCw size={14} className="mr-1" />Actualizar
          </Button>
          <Button onClick={exportCsv} size="sm" className="text-white" style={{ backgroundColor: '#457B9D' }} data-testid="marcas-export-csv-btn">
            <Download size={14} className="mr-1" />Exportar CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="relative md:col-span-2">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, email, asunto, mensaje..."
            className="pl-9 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
            data-testid="marcas-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="bg-gray-900 border-gray-700 text-white" data-testid="marcas-status-filter">
            <SelectValue placeholder="Todos los estados" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__ALL__">Todos los estados</SelectItem>
            {STATUS_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {/* Error banner — visible when fetch failed. */}
      {fetchError && (
        <div className="mx-6 mb-4 rounded-lg border border-red-700/60 bg-red-900/30 p-4 flex items-start gap-3" data-testid="marcas-fetch-error">
          <div className="mt-0.5">⚠️</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-red-200">No se pudieron cargar las consultas</div>
            <div className="text-xs text-red-300/80 mt-1 break-words">{fetchError}</div>
            <div className="text-[11px] text-gray-400 mt-2">
              Puede ser un problema temporal de red o que tu antivirus esté bloqueando la petición.
              Verificá tu conexión y volvé a intentar. Si el problema persiste, mandanos captura de este mensaje.
            </div>
          </div>
          <Button onClick={fetchItems} size="sm" variant="outline" className="border-red-700 text-red-100 hover:bg-red-800/40 shrink-0" data-testid="marcas-fetch-retry">
            <RefreshCw size={14} className="mr-1" />Reintentar
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="px-6 pb-12">
        <div className="overflow-x-auto border border-gray-800 rounded-lg">
          <table className="w-full text-sm" data-testid="marcas-table">
            <thead className="bg-gray-900">
              <tr className="text-left text-gray-400 uppercase text-xs">
                <th className="px-3 py-3">Fecha</th>
                <th className="px-3 py-3">Nombre / Empresa</th>
                <th className="px-3 py-3">Contacto</th>
                <th className="px-3 py-3">País</th>
                <th className="px-3 py-3">Web / RRSS</th>
                <th className="px-3 py-3">Asunto / Mensaje</th>
                <th className="px-3 py-3">Estado</th>
                <th className="px-3 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="text-gray-200">
              {loading && (<tr><td colSpan={8} className="text-center text-gray-500 py-10">Cargando...</td></tr>)}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center text-gray-500 py-10" data-testid="marcas-empty">
                  No hay consultas con esos filtros
                </td></tr>
              )}
              {!loading && filtered.map((it) => {
                const meta = statusMeta(it.status);
                const wa = whatsappLink(it.telefono);
                const web = webUrl(it.web);
                return (
                  <tr
                    key={it.id}
                    className="border-t border-gray-800 hover:bg-gray-900/50 cursor-pointer"
                    onClick={() => setSelected(it)}
                    data-testid={`marca-row-${it.id}`}
                  >
                    <td className="px-3 py-3 text-xs text-gray-400 whitespace-nowrap">{formatFecha(it.fecha)}</td>
                    <td className="px-3 py-3 font-medium">
                      <div>{it.nombre}</div>
                      {it.empresa ? <div className="text-xs text-gray-400" data-testid={`marca-row-empresa-${it.id}`}>{it.empresa}</div> : null}
                    </td>
                    <td className="px-3 py-3 text-xs">
                      <a href={`mailto:${it.email}`} onClick={(e) => e.stopPropagation()} className="text-blue-400 hover:underline">{it.email}</a>
                      {it.telefono && (
                        <div className="text-gray-400">
                          {wa
                            ? <a href={wa} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-green-400 hover:underline inline-flex items-center gap-1"><FaWhatsapp />{it.telefono}</a>
                            : it.telefono}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-xs">{it.pais || '—'}</td>
                    <td className="px-3 py-3 text-xs max-w-[180px] truncate">
                      {web
                        ? <a href={web} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-sky-400 hover:underline" data-testid={`marca-row-link-web-${it.id}`}>{it.web}</a>
                        : (it.web || '—')}
                    </td>
                    <td className="px-3 py-3 text-xs max-w-[260px]">
                      <div className="text-gray-200 truncate">{it.asunto || '—'}</div>
                      {it.mensaje ? <div className="text-gray-500 truncate">{it.mensaje}</div> : null}
                    </td>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <Select value={it.status || 'pendiente'} onValueChange={(v) => changeStatus(it, v)}>
                        <SelectTrigger
                          className="h-8 text-xs border-0 font-semibold"
                          style={{ backgroundColor: meta.color + '22', color: meta.color }}
                          data-testid={`marca-status-${it.id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" onClick={() => removeItem(it)} data-testid={`marca-delete-${it.id}`} className="text-red-400 hover:text-red-300 hover:bg-red-950/30">
                        <Trash2 size={14} />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <MarcaModal
        marca={selected}
        onClose={() => setSelected(null)}
        onSaved={(updated) => {
          setItems((arr) => arr.map((x) => x.id === updated.id ? updated : x));
          setSelected(null);
        }}
      />
    </div>
  );
};

// -------------------- PAGE ROOT --------------------
const InfluencersCRM = () => {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [section, setSection] = useState('talentos'); // 'talentos' | 'marcas'

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await influsMe();
        if (!cancelled) setAuthed(true);
      } catch { /* not authed */ }
      finally { if (!cancelled) setChecking(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleLogout = async () => {
    try { await influsLogout(); } catch { /* ignore */ }
    setAuthed(false);
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <p className="text-gray-500">Cargando...</p>
      </div>
    );
  }

  if (!authed) {
    // Session expired or never authenticated. If user came via /panel, bounce
    // them back to the unified Hub instead of showing the local login form.
    if (redirectToHubIfFromHub()) return null;
    return <LoginScreen onAuthed={() => setAuthed(true)} />;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Global header with section tabs */}
      <div className="border-b border-gray-800 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-6 flex-wrap">
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            Consultas
          </h1>
          <div className="flex rounded-lg overflow-hidden border border-gray-700" data-testid="section-tabs">
            <button
              onClick={() => setSection('talentos')}
              className="px-5 py-2 text-sm font-semibold uppercase tracking-wider transition-colors"
              style={{
                backgroundColor: section === 'talentos' ? '#1B6B3A' : 'transparent',
                color: section === 'talentos' ? 'white' : '#9CA3AF',
              }}
              data-testid="tab-talentos"
            >
              Talentos
            </button>
            <button
              onClick={() => setSection('marcas')}
              className="px-5 py-2 text-sm font-semibold uppercase tracking-wider transition-colors border-l border-gray-700"
              style={{
                backgroundColor: section === 'marcas' ? '#457B9D' : 'transparent',
                color: section === 'marcas' ? 'white' : '#9CA3AF',
              }}
              data-testid="tab-marcas"
            >
              Marcas
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <BackToPanelButton size="sm" className="bg-gray-900 text-white" />
          <Button onClick={handleLogout} variant="outline" size="sm" className="bg-gray-900 border-gray-700 text-white hover:bg-gray-800" data-testid="influs-logout-btn">
            <LogOut size={14} className="mr-1" />Salir
          </Button>
        </div>
      </div>

      {/* Access to Presupuestos (commercial tool) */}
      <div className="border-b border-gray-800 px-6 py-3 flex flex-wrap items-center gap-3 bg-gradient-to-r from-orange-900/10 to-transparent">
        <div className="text-sm text-gray-400 flex-1 min-w-0">
          <FileText size={14} className="inline mr-1 text-orange-400" />
          <span className="font-semibold text-orange-300">Herramientas comerciales:</span>{' '}
          Creá propuestas con talentos públicos y ocultos, generá PDFs listos para enviar.
        </div>
        <Link to="/panel/propuestas">
          <Button size="sm" className="text-white font-semibold" style={{ backgroundColor: '#F4A261' }} data-testid="influs-presupuestos-btn">
            <FileText size={14} className="mr-1" />Presupuestos
          </Button>
        </Link>
      </div>

      {section === 'talentos' ? <CRMTable onLogout={handleLogout} /> : <MarcasTable onLogout={handleLogout} />}
    </div>
  );
};

export default InfluencersCRM;
